import type { ApiResponse, Locale, ResponseError, ResponseSuccess, SiteEnum } from "@/types";
import { getApiHost } from "@/utils/env.ts";
import BadRequestException from "../exceptions/BadRequestException.ts";
import ServerErrorException from "../exceptions/ServerErrorException.ts";
import Logger from "./logger.ts";

const VERSION = "4.2";

type GetRequestMap = {
    "/catalog/product-configurator/": { smartnumber: string | undefined; modelcode: string | undefined };
    "/catalog/shortcode/resolve/": { input: string };
};

export const getRequestBuilder = requestFactory((site, locale) => {
    return async <
        S extends Record<string, unknown>,
        E extends Record<string, unknown>,
        P extends keyof GetRequestMap = keyof GetRequestMap,
    >(
        path: P,
        ...args: GetRequestMap[P] extends never ? [] : [data: GetRequestMap[P]]
    ): Promise<ApiResponse<S, E>> => {
        const [data] = args;

        const url = new URL(path, getApiHost());
        for (const [key, value] of Object.entries(data ?? {})) {
            if (!value) continue;

            url.searchParams.set(key, value);
        }

        const { millis, response } = await timeRequest(async () => {
            return fetch(url.toString(), { headers: getHeaders(site, locale, VERSION) });
        });

        Logger.request("GET", url.toString(), { responseCode: response.status, millis });

        return handleResponse<S, E>(response);
    };
});

type PostRequestConfig<T> = { dataAsSearchParam?: boolean } & T;
type PostRequestMap = {
    "/media/zip/generate-direct-from-configurator": PostRequestConfig<{ smartnumber: string }>;
};

export const postRequestBuilder = requestFactory((site, locale) => {
    return async <
        S extends Record<string, unknown>,
        E extends Record<string, unknown>,
        P extends keyof PostRequestMap = keyof PostRequestMap,
    >(
        path: P,
        ...args: PostRequestMap[P] extends never ? [] : [data: PostRequestMap[P]]
    ): Promise<ApiResponse<S, E>> => {
        const [data] = args;

        const { dataAsSearchParam = false, ...requestBody } = data ?? {};

        const url = new URL(path, getApiHost());
        if (dataAsSearchParam) {
            for (const [key, value] of Object.entries(requestBody ?? {})) {
                if (!value) continue;

                url.searchParams.set(key, `${value}`);
            }
        }

        const { millis, response } = await timeRequest(async () => {
            return fetch(url.toString(), {
                headers: getHeaders(site, locale, VERSION, { isJsonRequest: !dataAsSearchParam }),
                method: "POST",
                body: !dataAsSearchParam ? JSON.stringify(requestBody) : undefined,
            });
        });

        Logger.request("POST", url.toString(), { responseCode: response.status, millis });

        return handleResponse<S, E>(response);
    };
});

type Options = { isJsonRequest?: boolean; authorization?: string };

export const looseGetRequestBuilder = requestFactory((site, locale) => {
    return async <S extends Record<string, unknown>, E extends Record<string, unknown>>(
        url: URL,
        opts?: { authorization?: string; query?: Record<string, string | string[]> },
    ) => {
        if (opts?.query) {
            for (const [key, value] of Object.entries(opts.query)) {
                if (Array.isArray(value)) {
                    for (const entry of value) {
                        url.searchParams.append(key, entry);
                    }
                    continue;
                }

                url.searchParams.set(key, value);
            }
        }

        const { millis, response } = await timeRequest(async () => {
            return fetch(url.toString(), {
                headers: getHeaders(site, locale, VERSION, { authorization: opts?.authorization }),
            });
        });

        Logger.request("GET", url.toString(), { responseCode: response.status, millis });

        return handleResponse<S, E>(response);
    };
});

export const loosePostRequestBuilder = requestFactory((site, locale) => {
    return async <S extends Record<string, unknown>, E extends Record<string, unknown>>(
        url: URL,
        body?: Record<string, unknown>,
        opts?: { authorization?: string },
    ) => {
        const { millis, response } = await timeRequest(async () => {
            return fetch(url.toString(), {
                headers: getHeaders(site, locale, VERSION, {
                    isJsonRequest: !!body,
                    authorization: opts?.authorization,
                }),
                method: "POST",
                body: !!body ? JSON.stringify(body) : undefined,
            });
        });

        Logger.request("POST", url.toString(), { responseCode: response.status, millis });

        return handleResponse<S, E>(response);
    };
});

async function handleResponse<S extends Record<string, unknown>, E extends Record<string, unknown>>(
    response: Response,
): Promise<ApiResponse<S, E>> {
    if (response.ok) {
        const data = await response.json();

        return { success: true, ...data } as ResponseSuccess<S>;
    }

    const { status } = response;
    const contentType = response.headers.get("content-type");
    const isJsonResponse = contentType?.includes("json");

    if (!isJsonResponse) throw new BadRequestException("Failed parsing data");

    const json = await response.json();

    if (status >= 500) {
        const data = json as { message?: string };
        throw new ServerErrorException(data?.message);
    }

    return { success: false, ...json } as ResponseError<E>;
}

function requestFactory<R>(requester: (site: SiteEnum, locale: Locale) => R) {
    return requester;
}

function getHeaders(site: string, locale: string, version: string, opts?: Options) {
    const headers = new Headers();

    headers.set("Site", site);
    headers.set("Locale", locale);
    headers.set("Version", version);

    if (opts?.isJsonRequest) {
        headers.set("Content-Type", "application/json");
    }

    if (opts?.authorization) {
        headers.set("Authorization", `Bearer ${opts.authorization}`);
    }

    return headers;
}

async function timeRequest(request: () => Promise<Response>) {
    const start = performance.now();
    const response = await request();
    const stop = performance.now();

    return { response, millis: (stop - start).toFixed(3) };
}
